// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { type Country } from '@tectonics/prs/country';
import { useIncomingPayment, getCountry, getReceipts } from '@tectonics/prs/incoming-payment';
import { getInstalmentsFromReceipts, type Receipt } from '@tectonics/prs/receipts';
import { getInstalmentsNumber, getAmountPartialFirst } from '@tectonics/prs/receipts/receipt/instalments-plan';
import { getSafeInstalmentsPlan, getReceiptAmountLocalTotal } from '@tectonics/prs/receipts/receipt';
import { useUserInputAsSelect } from '@tectonics/prs/user-input-values';
import { amountToCurrencyString } from '@tectonics/prs/money';
import { useSubmitting } from '@tectonics/prs/submitting';
import { Empty, Select, SelectOption, Label } from '../../../atoms';
import { getMoneyFormat } from '../../../utils/money-format';
import { Field } from '../../../molecules';

export function FieldCardInstalments(): Node {
  const inputId = useUniqueId();
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  const receipts = getReceipts(incomingPayment);
  const instalments = getInstalmentsFromReceipts(receipts);
  const [instalmentNumber, handleChangeInstalmentNumber] = useUserInputAsSelect('selectedInstalment');
  const submitting = useSubmitting();

  if (instalments.length === 1)
    return <Empty />;

  return (
    <Field
      label={<Label htmlFor={inputId}><LabelMessage country={country} /></Label>}
      showFloatingLabel
      data-test-name="card-instalments"
    >
      <Select
        id={inputId}
        value={instalmentNumber}
        onChange={handleChangeInstalmentNumber}
        readOnly={submitting}
        name="card-instalments"
      >
        {instalments.map(instalment => {
          const instalmentsPlan = getSafeInstalmentsPlan(instalment);
          const instalmentsNumber = getInstalmentsNumber(instalmentsPlan);

          return (
            <InstalmentOption
              key={instalmentsNumber}
              instalment={instalment}
              instalmentsNumber={instalmentsNumber}
              country={country}
            />
          );
        })}
      </Select>
    </Field>
  );
}

type InstalmentOptionProps = {|
  instalment: Receipt,
  instalmentsNumber: number,
  country: Country,
|};

function InstalmentOption({ instalment, instalmentsNumber, country }: InstalmentOptionProps): Node {
  const formatAmount = amountToCurrencyString(getMoneyFormat(country));
  const instalmentsPlan = getSafeInstalmentsPlan(instalment);

  const totalStringToDisplay =
    instalmentsNumber > 1 ? `(Total: ${formatAmount(getReceiptAmountLocalTotal(instalment))})` : '';

  return (
    <SelectOption value={instalmentsNumber}>
      {`${instalmentsNumber}x de ${formatAmount(getAmountPartialFirst(instalmentsPlan))} ${totalStringToDisplay}`}
    </SelectOption>
  );
}

function LabelMessage(props: { country: Country }): Node {
  switch (props.country) {
    case 'br':
      return 'Parcelas';
    case 'ar':
    case 'cl':
    case 'co':
    case 'mx':
    case 'uy':
    case 'py':
      return 'Cuotas';
    default:
      throw new Error(`Country "${props.country}" not supported.`);
  }
}
